/* The Financial Architect. Motion and interaction layer. Vanilla JS only. */
(function () {
  'use strict';

  var root = document.documentElement;
  root.classList.remove('no-js');

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var coarse = window.matchMedia('(pointer: coarse)').matches;

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  /* ---------------------------------------------------------------- reveal */
  function initReveal(scope) {
    var items = (scope || document).querySelectorAll('[data-reveal]:not(.is-in)');
    if (!items.length) return;
    if (reduced || !('IntersectionObserver' in window)) {
      items.forEach(function (el) { el.classList.add('is-in'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        var group = el.parentElement ? el.parentElement.querySelectorAll(':scope > [data-reveal]') : [];
        var index = Array.prototype.indexOf.call(group, el);
        if (index > 0) el.style.setProperty('--reveal-delay', Math.min(index, 8) * 70 + 'ms');
        el.classList.add('is-in');
        io.unobserve(el);
      });
    }, { rootMargin: '0px 0px -12% 0px', threshold: 0.08 });
    items.forEach(function (el) { io.observe(el); });
  }

  /* --------------------------------------------------------------- header */
  function initHeader() {
    var header = document.querySelector('[data-header]');
    if (!header) return;
    var last = -1;
    function onScroll() {
      var y = window.scrollY > 24;
      if (y === last) return;
      last = y;
      header.classList.toggle('is-stuck', y);
    }
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* --------------------------------------------------------------- drawer */
  function initDrawer() {
    var drawer = document.querySelector('[data-drawer]');
    var openers = document.querySelectorAll('[data-drawer-open]');
    if (!drawer || !openers.length) return;
    var panel = drawer.querySelector('.drawer__panel');
    var lastFocus = null;

    function open() {
      lastFocus = document.activeElement;
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      var first = panel.querySelector('button, a');
      if (first) first.focus();
      document.addEventListener('keydown', onKey);
    }
    function close() {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onKey);
      if (lastFocus) lastFocus.focus();
    }
    function onKey(e) {
      if (e.key === 'Escape') { close(); return; }
      if (e.key !== 'Tab') return;
      var focusables = panel.querySelectorAll('a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])');
      if (!focusables.length) return;
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }

    openers.forEach(function (b) { b.addEventListener('click', open); });
    drawer.querySelectorAll('[data-drawer-close]').forEach(function (b) {
      b.addEventListener('click', close);
    });

    drawer.querySelectorAll('.drawer__toggle').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var sub = document.getElementById(btn.getAttribute('aria-controls'));
        if (!sub) return;
        var open = sub.classList.toggle('is-open');
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    });
  }

  /* ------------------------------------------------------------ accordion */
  function initAccordions() {
    document.querySelectorAll('[data-acc-btn]').forEach(function (btn) {
      var panel = document.getElementById(btn.getAttribute('aria-controls'));
      if (!panel) return;
      btn.addEventListener('click', function () {
        var expanded = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', expanded ? 'false' : 'true');
        panel.style.height = expanded ? '0px' : panel.scrollHeight + 'px';
      });
      panel.addEventListener('transitionend', function () {
        if (btn.getAttribute('aria-expanded') === 'true') panel.style.height = 'auto';
      });
    });
  }

  /* ----------------------------------------------------------------- tabs */
  function initTabs() {
    document.querySelectorAll('[data-tabs]').forEach(function (group) {
      var tabs = group.querySelectorAll('[role="tab"]');
      function select(tab) {
        tabs.forEach(function (t) {
          var on = t === tab;
          t.setAttribute('aria-selected', on ? 'true' : 'false');
          t.tabIndex = on ? 0 : -1;
          var p = document.getElementById(t.getAttribute('aria-controls'));
          if (p) p.hidden = !on;
        });
      }
      tabs.forEach(function (tab, i) {
        tab.addEventListener('click', function () { select(tab); });
        tab.addEventListener('keydown', function (e) {
          var next = null;
          if (e.key === 'ArrowRight') next = tabs[(i + 1) % tabs.length];
          if (e.key === 'ArrowLeft') next = tabs[(i - 1 + tabs.length) % tabs.length];
          if (!next) return;
          e.preventDefault();
          next.focus();
          select(next);
        });
      });
    });
  }

  /* ----------------------------------------------------------- steps line */
  function initSteps() {
    var tracks = document.querySelectorAll('[data-steps]');
    if (!tracks.length) return;
    if (reduced || !('IntersectionObserver' in window)) {
      tracks.forEach(function (t) { t.style.setProperty('--progress', 1); });
      document.querySelectorAll('.step').forEach(function (s) { s.classList.add('is-in'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var track = entry.target;
        track.style.setProperty('--progress', 1);
        track.querySelectorAll('.step').forEach(function (step, i) {
          setTimeout(function () { step.classList.add('is-in'); }, 220 + i * 180);
        });
        io.unobserve(track);
      });
    }, { threshold: 0.25 });
    tracks.forEach(function (t) { io.observe(t); });
  }

  /* ----------------------------------------------------------------- rail */
  function initRails() {
    document.querySelectorAll('[data-rail]').forEach(function (rail) {
      var prev = document.querySelector('[data-rail-prev="' + rail.id + '"]');
      var next = document.querySelector('[data-rail-next="' + rail.id + '"]');
      function step() { return Math.max(rail.clientWidth * 0.72, 260); }
      function sync() {
        if (!prev || !next) return;
        prev.disabled = rail.scrollLeft <= 4;
        next.disabled = rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 4;
      }
      if (prev) prev.addEventListener('click', function () { rail.scrollBy({ left: -step(), behavior: reduced ? 'auto' : 'smooth' }); });
      if (next) next.addEventListener('click', function () { rail.scrollBy({ left: step(), behavior: reduced ? 'auto' : 'smooth' }); });
      rail.addEventListener('scroll', sync, { passive: true });
      sync();

      if (coarse) return;
      var down = false, startX = 0, startLeft = 0, moved = false;
      rail.addEventListener('pointerdown', function (e) {
        if (e.target.closest('a')) { /* still allow drag but track movement */ }
        down = true; moved = false;
        startX = e.clientX;
        startLeft = rail.scrollLeft;
        rail.classList.add('is-dragging');
      });
      window.addEventListener('pointermove', function (e) {
        if (!down) return;
        var dx = e.clientX - startX;
        if (Math.abs(dx) > 4) moved = true;
        rail.scrollLeft = startLeft - dx;
      });
      window.addEventListener('pointerup', function () {
        if (!down) return;
        down = false;
        rail.classList.remove('is-dragging');
      });
      rail.addEventListener('click', function (e) {
        if (moved) { e.preventDefault(); moved = false; }
      }, true);
    });
  }

  /* ------------------------------------------------------------- magnetic */
  function initMagnetic() {
    if (reduced || coarse) return;
    document.querySelectorAll('[data-magnetic]').forEach(function (el) {
      var strength = parseFloat(el.getAttribute('data-magnetic')) || 0.28;
      el.addEventListener('pointermove', function (e) {
        var r = el.getBoundingClientRect();
        var x = (e.clientX - r.left - r.width / 2) * strength;
        var y = (e.clientY - r.top - r.height / 2) * strength;
        el.style.transform = 'translate3d(' + x + 'px,' + y + 'px,0)';
      });
      el.addEventListener('pointerleave', function () { el.style.transform = ''; });
    });
  }

  /* ----------------------------------------------------------------- tilt */
  function initTilt() {
    if (reduced || coarse) return;
    document.querySelectorAll('[data-tilt]').forEach(function (el) {
      var max = parseFloat(el.getAttribute('data-tilt')) || 5;
      el.addEventListener('pointermove', function (e) {
        var r = el.getBoundingClientRect();
        var px = (e.clientX - r.left) / r.width - 0.5;
        var py = (e.clientY - r.top) / r.height - 0.5;
        el.style.transform = 'perspective(1000px) rotateY(' + (px * max) + 'deg) rotateX(' + (-py * max) + 'deg)';
      });
      el.addEventListener('pointerleave', function () { el.style.transform = ''; });
    });
  }

  /* --------------------------------------------------------- reading flow */
  function initReading() {
    var bar = document.querySelector('[data-progress]');
    var content = document.querySelector('[data-article-content]');
    if (bar && content) {
      var tick = function () {
        var rect = content.getBoundingClientRect();
        var total = rect.height - window.innerHeight;
        var passed = Math.min(Math.max(-rect.top, 0), Math.max(total, 1));
        var ratio = total > 0 ? passed / total : 0;
        bar.style.transform = 'scaleX(' + ratio + ')';
      };
      tick();
      window.addEventListener('scroll', tick, { passive: true });
      window.addEventListener('resize', tick);
    }

    var toc = document.querySelector('[data-toc]');
    if (!toc || !content) return;
    var headings = content.querySelectorAll('h2, h3');
    if (!headings.length) { toc.closest('[data-toc-wrap]') && toc.closest('[data-toc-wrap]').remove(); return; }
    var list = document.createElement('ol');
    var links = [];
    headings.forEach(function (h, i) {
      if (!h.id) h.id = 'section-' + (i + 1);
      var li = document.createElement('li');
      li.className = h.tagName === 'H3' ? 'lvl-3' : 'lvl-2';
      var a = document.createElement('a');
      a.href = '#' + h.id;
      a.textContent = h.textContent;
      li.appendChild(a);
      list.appendChild(li);
      links.push(a);
    });
    toc.appendChild(list);

    if (!('IntersectionObserver' in window)) return;
    var spy = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        links.forEach(function (a) {
          a.classList.toggle('is-active', a.getAttribute('href') === '#' + entry.target.id);
        });
      });
    }, { rootMargin: '-15% 0px -70% 0px' });
    headings.forEach(function (h) { spy.observe(h); });
  }

  /* ---------------------------------------------------------------- share */
  function initShare() {
    document.querySelectorAll('[data-share]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        var type = btn.getAttribute('data-share');
        var url = window.location.href;
        var title = document.title;
        if (type === 'copy') {
          e.preventDefault();
          if (navigator.clipboard) {
            navigator.clipboard.writeText(url).then(function () {
              var old = btn.getAttribute('aria-label');
              btn.setAttribute('aria-label', 'Link copied');
              setTimeout(function () { btn.setAttribute('aria-label', old); }, 1800);
            });
          }
          return;
        }
        if (type === 'native' && navigator.share) {
          e.preventDefault();
          navigator.share({ title: title, url: url });
        }
      });
    });
  }

  /* --------------------------------------------------------- page transition */
  function initVeil() {
    if (reduced) return;
    var veil = document.querySelector('[data-veil]');
    if (!veil) return;
    window.addEventListener('pageshow', function () { veil.classList.remove('is-on'); });
    document.addEventListener('click', function (e) {
      var a = e.target.closest('a');
      if (!a) return;
      if (a.target === '_blank' || a.hasAttribute('download')) return;
      var href = a.getAttribute('href') || '';
      if (!href || href.charAt(0) === '#' || href.indexOf('mailto:') === 0 || href.indexOf('tel:') === 0) return;
      if (a.hostname && a.hostname !== window.location.hostname) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey) return;
      e.preventDefault();
      veil.classList.add('is-on');
      setTimeout(function () { window.location.href = a.href; }, 260);
    });
  }

  ready(function () {
    initReveal(document);
    initHeader();
    initDrawer();
    initAccordions();
    initTabs();
    initSteps();
    initRails();
    initMagnetic();
    initTilt();
    initReading();
    initShare();
    initVeil();
  });

  /* Theme editor support */
  document.addEventListener('shopify:section:load', function (e) {
    initReveal(e.target);
    initHeader();
    initDrawer();
    initAccordions();
    initTabs();
    initSteps();
    initRails();
    initMagnetic();
    initTilt();
    initReading();
    initShare();
  });
})();
